/**
 * 🚀 Shopify Vanilla JS Recommend Engine (Ultimate Complete Version)
 * Features include:
 * 1. Cart State Tracking (Real-time sync with authoritative /cart.js, completely resolving data disconnect/single-item bugs)
 * 2. Independent Add-to-Cart Interception (Prevents double item additions caused by nested forms)
 * 3. 🛡️ Smart DOM Debounce Fingerprint Lock (Completely resolves the "You May Also Like" flickering issue when the cart changes)
 * 4. 🛍️ Compatible with async VegoCart script awakening and native Ajax drawer pop-ups
 * 5. ⚡ Advanced Debounce & Memory Leak Protection (SessionStorage Quota & Script Revive limits)
 */
(function () {
  "use strict";

  const CONFIG = {
    POOL_SIZE: 10,
    CART_EMPTY_SECTION_ID: "cart-empty-recommends",
    CART_SECTION_ID: "cart-recommend-products",
    PDP_SECTION_ID: "product-recommend-cards",
    EXCLUDED_HANDLES: ["routeins"],
    CART_DISPLAY_LIMIT: 3,
    PDP_DISPLAY_LIMIT: 2,
    ASSEMBLY_DISPLAY_LIMIT: 3,
    SEEDLING_SECTION_LIMIT: 12,
  };

  // ==========================================
  // 1. Global Cart Tracking (Authoritative data sync, prevents 429 errors)
  // ==========================================
  window.ShopifyCartTracker = {
    init() {
      window.ShopifyCart = window.ShopifyCart || { items: [] };
      this.bindInterceptors();
    },
    getCart() {
      return window.ShopifyCart;
    },
    bindInterceptors() {
      const originalFetch = window.fetch;
      const self = this;
      window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";

        if (
          url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/update") ||
          url.includes("/cart/clear")
        ) {
          const cloned = response.clone();
          cloned
            .json()
            .then((data) => {
              if (url.includes("/cart/add")) {
                // Handle both single item add and multiple items add without overwriting the cart
                const addedItems = data.items ? data.items : [data];

                addedItems.forEach((addedItem) => {
                  const targetId = addedItem.variant_id || addedItem.id;
                  if (targetId) {
                    const existing = window.ShopifyCart.items.find(
                      (item) => (item.variant_id || item.id) === targetId,
                    );
                    if (existing) {
                      existing.quantity += addedItem.quantity || 1;
                    } else {
                      window.ShopifyCart.items.push(addedItem);
                    }
                  }
                });
              } else {
                // Only /cart/change, /cart/update, and /cart/clear return the full cart object
                data.items = data.items.filter((item) => item.item_count>0); 
                window.ShopifyCart = data;
              }

              self.notifyComponents();
            })
            .catch(() => {});
        }
        return response;
      };
    },
    notifyComponents() {
      // 🛡️ Debounce：300ms
      if (this._notifyTimer) clearTimeout(this._notifyTimer);
      this._notifyTimer = setTimeout(() => {
        if (window.CartRecommendationsAPI)
          window.CartRecommendationsAPI.refresh();
        //if (window.ProductRecommendationsAPI) window.ProductRecommendationsAPI.refresh();
      }, 300);
    },
  };
  window.ShopifyCartTracker.init();

  // ==========================================
  // 2. Core Utility Functions and Data Parsing
  // ==========================================
  function isExcludedHandle(handle) {
    return Boolean(handle && CONFIG.EXCLUDED_HANDLES.includes(handle));
  }
  function getDefaultAnchorIds() {
    //  if (theme.isAu()) return ["empty"];
    //  if (theme.isCa()) return ["empty"];
    return ["empty"];
  }
  function getSortedCartProductIds(cartItems) {
    if (!cartItems || !cartItems.length) return getDefaultAnchorIds();
    const uniqueItems = [];
    const seen = new Set();
    for (const item of cartItems) {
      const pid = item.product_id || item.productId;
      if (!seen.has(pid)) {
        seen.add(pid);
        uniqueItems.push(item);
      }
    }
    uniqueItems.sort((a, b) => {
      const priceA = a.original_line_price || a.price || 0;
      const priceB = b.original_line_price || b.price || 0;
      return priceB - priceA;
    });
    let res = uniqueItems.map((item) => item.product_id || item.productId);
    if (res.length == 0) res = getDefaultAnchorIds();
    return res;
  }

  function extractHandleFromElement(item) {
    let handle = item.dataset.productHandle || item.dataset.handle;
    if (handle) return handle;
    const link = item.querySelector('a[href*="/products/"]');
    if (link) {
      const href = link.getAttribute("href");
      let rawHandle = href
        .split("/products/")[1]
        .split("?")[0]
        .split("#")[0]
        .replace(/\//g, "");
      try {
        return decodeURIComponent(rawHandle);
      } catch (e) {
        return rawHandle;
      }
    }
    return null;
  }

  // Precise variant positioning engine during the component rendering phase
  function resolveVariantId(card) {
    const recommendItem =
      card.closest(".product-recommend-item") ||
      (card.classList.contains("product-recommend-item") ? card : null);
    if (recommendItem) {
      const recommendSelect = recommendItem.querySelector(
        'select[name="items[1][id]"]',
      );
      if (recommendSelect?.value) return recommendSelect.value;
      const recommendId = recommendItem.querySelector(
        'input[name="items[1][id]"]',
      );
      if (recommendId?.value) return recommendId.value;
    }

    const form = card.closest("form") || card.querySelector("form");
    if (!form) return null;

    const variantJson = form.querySelector(".variant-json");
    const selects = form.querySelectorAll("select.grid-product-variant-input");

    if (variantJson && selects.length) {
      let variants;
      try {
        variants = JSON.parse(variantJson.textContent);
      } catch (e) {
        variants = null;
      }
      if (variants?.length) {
        for (const sel of selects) {
          if (
            sel.value &&
            (sel.name === "items[1][id]" || sel.dataset.select === "variant")
          ) {
            return String(sel.value);
          }
        }
        const selected = {};
        selects.forEach((sel) => {
          if (sel.dataset.index) selected[sel.dataset.index] = sel.value;
        });
        if (Object.keys(selected).length) {
          const match = variants.find((variant) =>
            Object.entries(selected).every(
              ([key, value]) => variant[key] === value,
            ),
          );
          if (match) return String(match.id);
        }
      }
    }

    const idField = form.querySelector('select[name="id"], input[name="id"]');
    if (idField?.value) return idField.value;

    if (variantJson) {
      try {
        const variants = JSON.parse(variantJson.textContent);
        if (variants?.length) return String(variants[0].id);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function collectLineItemProperties(form, prefix) {
    const properties = {};
    form.querySelectorAll('input[type="hidden"], textarea').forEach((el) => {
      if (!el.name) return;
      let propName = null;
      if (prefix) {
        const head = `${prefix}[properties][`;
        if (el.name.startsWith(head) && el.name.endsWith("]")) {
          propName = el.name.slice(head.length, -1);
        }
      } else if (el.name.startsWith("properties[") && el.name.endsWith("]")) {
        propName = el.name.slice("properties[".length, -1);
      }
      if (propName) properties[propName] = el.value;
    });
    return properties;
  }

  function buildCartAddPayload(card) {
    const recommendItem = card.closest(".product-recommend-item");
    const form = card.closest("form") || card.querySelector("form");
    if (!form) return null;

    const variantId = resolveVariantId(card);
    if (!variantId) return null;

    const item = { id: variantId, quantity: 1 };
    let properties = {};
    if (recommendItem) {
      properties = {
        ...collectLineItemProperties(form),
        ...collectLineItemProperties(form, "items[1]"),
      };
    } else {
      properties = collectLineItemProperties(form);
    }
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  function initCartRecommendVariants(container) {
    if (!window.jQuery) return;
    const $container = window.jQuery(container);
    if (
      typeof $container.find(".grid-product").collectionProduct === "function"
    ) {
      $container.find(".grid-product").collectionProduct();
    } else if (
      typeof $container.find(".grid-product").collecionProduct === "function"
    ) {
      $container.find(".grid-product").collecionProduct();
    }
    if (typeof window.theme?.reRenderOption === "function") {
      window.theme.reRenderOption();
    }
  }

  function stripPdpAnchorFields(root) {
    root
      .querySelectorAll('.product-recommend-item [name^="items[0]"]')
      .forEach((field) => {
        field.remove();
      });
  }

  async function fetchRecommendations(productId, sectionId) {
    // 🌍 Get market fingerprint,  fallback to default.
    const marketFingerprint =
      window.Shopify?.currency?.active ||
      window.Shopify?.country ||
      window.Shopify?.routes?.root ||
      "default";
    const isEmptyCart = productId == "empty";
    if (isEmptyCart) {
      if (theme.isAu()) {
        productId = "7064465014819";
      } else if (theme.isCa()) {
        productId = "4563721682979";
      } else {
        productId = "4563721682979";
      }
    }
    // 🔑 Set market fingerprint to cache key.
    const cacheKey = `shopify-rec-${isEmptyCart ? "empty-" : ""}${marketFingerprint}-${sectionId}-${productId}`;

    const localCache = sessionStorage.getItem(cacheKey);
    if (localCache) {
      const doc = new DOMParser().parseFromString(localCache, "text/html");
      return Array.from(
        doc.querySelectorAll(
          ".vego-cart-recommend-item, .product-recommend-item",
        ),
      );
    }

    const baseUrl =
      window.Shopify?.routes?.product_recommendations_url ||
      "/recommendations/products";
    const url = `${baseUrl}?section_id=${sectionId}&product_id=${productId}&limit=${CONFIG.POOL_SIZE}&intent=related`;
    try {
      const res = await fetch(url);
      const htmlText = await res.text();

      // 🛡️ Session overflow protect!
      try {
        sessionStorage.setItem(cacheKey, htmlText);
      } catch (e) {
        console.warn("Recommendation cache full, skipping storage.");
      }

      const doc = new DOMParser().parseFromString(htmlText, "text/html");
      return Array.from(
        doc.querySelectorAll(
          ".vego-cart-recommend-item, .product-recommend-item",
        ),
      );
    } catch (e) {
      return [];
    }
  }

  // ======== Assembly & seedling related Start ========
  function normalizeHandleInput(handle) {
    if (Array.isArray(handle)) return handle[0] || null;
    return handle || null;
  }

  async function waitForAjaxSingleProduct(maxAttempts = 80) {
    let attempts = 0;
    while (
      typeof window._ajaxSingleProduct !== "function" &&
      attempts < maxAttempts
    ) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
    return typeof window._ajaxSingleProduct === "function";
  }

  async function ajaxSingleProduct(entry) {
    const payload =
      typeof entry === "string"
        ? { handle: entry }
        : entry?.handle
          ? entry
          : { handle: normalizeHandleInput(entry) };
    if (!payload?.handle) return null;

    const ready = await waitForAjaxSingleProduct();
    if (!ready) return null;
    return window._ajaxSingleProduct(payload);
  }

  async function fetchOneRecommendProduct(handles, index, results, limit) {
    if (results.length >= limit || index >= handles.length) {
      return index;
    }

    const entry = handles[index];
    if (
      !entry?.product &&
      !Object.prototype.hasOwnProperty.call(entry || {}, "error")
    ) {
      if (!entry) {
        return fetchOneRecommendProduct(handles, index + 1, results, limit);
      }
      const res = await ajaxSingleProduct(entry);
      if (!res) {
        entry.error = true;
        if (index + 1 < handles.length) {
          return fetchOneRecommendProduct(handles, index + 1, results, limit);
        }
        return null;
      }
      entry.product = res.product;
      results.push(entry);
      if (results.length >= limit) return index + 1;
      return fetchOneRecommendProduct(handles, index + 1, results, limit);
    }

    if (entry?.error) {
      return fetchOneRecommendProduct(handles, index + 1, results, limit);
    }

    if (entry) {
      results.push(entry);
      if (results.length >= limit) return index + 1;
      return fetchOneRecommendProduct(handles, index + 1, results, limit);
    }

    return index;
  }

  function fetchRecommendProductsLimit(handles, offset, limit) {
    if (!handles?.length) return Promise.resolve([[], 0]);
    if (handles.length < limit) {
      limit = handles.length;
      offset = 0;
    }
    return new Promise((resolve) => {
      const results = [];
      const indexOffset = offset % handles.length;
      fetchOneRecommendProduct(handles, indexOffset, results, limit).then(
        (index) => {
          const seen = {};
          const unique = results.filter((item) => {
            if (!item.handle || seen[item.handle]) return false;
            seen[item.handle] = true;
            return true;
          });
          resolve([unique, index]);
        },
      );
    });
  }

  async function ensureThemeTemplate(name) {
    window.theme = window.theme || {};
    theme.templates = theme.templates || {};
    theme.templates[name] = theme.templates[name] || {};
    if (typeof theme.templates[name].result === "function") return;

    let attempts = 0;
    while (typeof theme.loadTemplate !== "function" && attempts < 80) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
    if (typeof theme.loadTemplate !== "function") return;

    const result = await theme.loadTemplate(name);
    theme.templates[name].result = result;
  }

  function syncAssemblyRecommendVariant(selectEl) {
    if (
      !selectEl ||
      !window.jQuery ||
      typeof window.formatVariantDataByDiscountPriceData !== "function"
    ) {
      return;
    }
    const $ = window.jQuery;
    const $item = $(selectEl).closest(".product-recommend-item");
    if (!$item.length) return;

    const variantJsonEl = $item.find(".variant-json");
    if (!variantJsonEl.length) return;

    let variants;
    try {
      variants = window.formatVariantDataByDiscountPriceData(
        JSON.parse(variantJsonEl.val() || variantJsonEl.text()),
      );
    } catch (e) {
      return;
    }

    const variantId = selectEl.value;
    let currentVariant;
    for (let i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        currentVariant = variants[i];
        break;
      }
    }
    if (!currentVariant) return;

    $item.find(".grid-product__color-image").removeClass("is-active");
    $item
      .find('.grid-product__color-image[data-variant-id="' + variantId + '"]')
      .addClass("is-active");
    $item.find(".gPreorderProperty1").remove();

    if (currentVariant.preOrder) {
      $item
        .find(".gFullfillProperty1")
        .val(`Ships by: ${currentVariant.fulfillmentDate}`);
      $item.append(
        '<input type="hidden" class="gPreorderProperty1" name="properties[_is_preorder]" value="Note: Pre-order item">',
      );
    } else {
      $item.find(".gFullfillProperty1").val("In Stock");
    }

    if (currentVariant.bundleDiscount) {
      $item.find(".btn-text").text(`Add +${currentVariant.savePrice}`);
      $item
        .find(".btn-text-save")
        .text(`Save $${currentVariant.bundleDiscount}`);
    } else {
      $item.find(".btn-text").text(`Add +$${currentVariant.price}`);
    }
  }

  async function waitForAssemblyPlugins(maxAttempts = 80) {
    let attempts = 0;
    while (attempts < maxAttempts) {
      if (
        window.jQuery &&
        typeof window.jQuery.fn.vselect === "function" &&
        typeof window.jQuery.fn.collecionProduct === "function"
      ) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }
    return Boolean(window.jQuery);
  }

  async function initAssemblyRecommendVariants(container) {
    await waitForAssemblyPlugins();
    if (!window.jQuery) return;
    const $ = window.jQuery;
    const $scope = $(container);
    try {
      window.$$?.updateInput?.();
      window.updateTagPosition?.();
      if (typeof $.fn.collecionProduct === "function") {
        $scope.find(".product-recommend-item").each(function () {
          try {
            $(this).collecionProduct();
          } catch (e) {
            console.log(e);
          }
        });
      }
      if (typeof $.fn.vselect === "function") {
        $scope.find("select.grid-product-variant-input:enabled").vselect();
      }
      $scope
        .find("select[name='items[1][id]'], select.grid-product-variant-input")
        .each(function () {
          syncAssemblyRecommendVariant(this);
        });
    } catch (e) {
      console.log(e);
    }
  }

  function initSeedlingRecommendCarousel(container) {
    if (!window.jQuery) return;
    const $ = window.jQuery;
    try {
      $(".page-section-featured-products-box").removeClass("hide");
      $(
        ".page-section-featured-products-box .feature-add-ons-action .grid-product__group",
      ).slick?.("unslick");
    } catch (e) {
      console.log(e);
    }
    window.$$?.updateInput?.();
    window.theme?.gridAddOns?.();
    $(container).find("select.grid-product-variant-input:enabled").vselect?.();
    if (
      typeof $(container).find(".grid-product").collecionProduct === "function"
    ) {
      $(container).find(".grid-product").collecionProduct();
    }
    $(".page-section-featured-products-box").removeClass("hide");
    window.yotpoWidgetsContainer?.initWidgets?.();
    $('[data-toggle="tooltip"]').tooltip?.();
  }
  // ======== Assembly & seedling related End ========

  // ==========================================
  // 3. 💣 Independent Event Control & Safe Add-to-Cart Interception
  // ==========================================
  function reviveScripts(container) {
    container
      .querySelectorAll("script:not([data-revived])")
      .forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) =>
          newScript.setAttribute(attr.name, attr.value),
        );
        if (oldScript.innerHTML) {
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        }
        newScript.setAttribute("data-revived", "true");
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
  }
  function getPDPForm(e) {
    let isPDPRecommends = e.target.closest("product-recommendations");
    if (isPDPRecommends) {
      let pdpSubmitBtnText = isPDPRecommends
        .closest("div")
        .querySelector(`form button[name="add"]`)
        .textContent.trim();
      if (!pdpSubmitBtnText.includes("Notify")) {
        let pdpForm = decodeURI(
          theme.utils.serialize(
            isPDPRecommends.closest("div").querySelector("form"),
          ),
        )
          .split("&")
          .reduce((res, item) => {
            let items = item.split("=");
            if (items[0].includes("properties")) {
              if (!res.properties) res.properties = {};
              let key = items[0]
                .replace("properties", "")
                .replace("[", "")
                .replace("]", "");
              //if (key != "__is_bundleProduct") 
              res.properties[key] = decodeURIComponent(items[1]);
            } else {
              res[items[0]] = items[1];
            }
            return res;
          }, {});
        console.log(pdpForm);
        return pdpForm;
      }
    }
    return null;
  }
  function handleSafeAddToCart(e) {
    const btn = e.target.closest(
      'button[type="submit"], button.add-to-cart, button[name="add"]',
    );
    if (!btn) return;
    if (
      btn.classList.contains("refresh-alsolike") ||
      btn.classList.contains("product-recommend-refresh")
    )
      return;

    const card = btn.closest(
      ".vego-cart-recommend-item, .product-recommend-item, .grid-product",
    );
    if (!card) return;

    if (btn.classList.contains("btn--loading")) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const recommendItem = card.closest(".product-recommend-item");
    if (recommendItem && window.jQuery) {
      const select = recommendItem.querySelector('select[name="items[1][id]"]');
      const plugin = select ? window.jQuery(select).data("vego.select") : null;
      if (plugin?.currentValue && select) {
        select.value = String(plugin.currentValue);
      }
    }

    const lineItem = buildCartAddPayload(card);
    if (!lineItem) return;

    btn.classList.add("btn--loading");

    let items = [lineItem];

    let pdpForm = getPDPForm(e);
    if (pdpForm) items.push(pdpForm);

    // Independent, clean, and precise Add-to-Cart request
    fetch((window.Shopify?.routes?.root || "/") + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    })
      .then((res) => res.json())
      .then(async (data) => {
        btn.classList.remove("btn--loading");

        if (data.status === 422 || data.description) {
          console.warn(data.description || data.message);
          return;
        }

        // 🌟 Precisely awaken the core asynchronous cart script
        if (typeof window.theme?.defer?.loadVegoCartScript === "function") {
          await window.theme.defer.loadVegoCartScript();
        }

        // 🌟 Trigger the global custom Ajax add-to-cart modal/sidebar pull-out event
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: {
              product: data,
              drawer: true,
              addToCartBtn: btn,
            },
          }),
        );
      })
      .catch(() => {
        btn.classList.remove("btn--loading");
      });
  }

  // ==========================================
  // 4. Component: Sidebar Cart Recommendations (<cart-recommendations>)
  // ==========================================
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.itemsContainer = this.querySelector(
        ".vego-cart-recommend-item-content",
      );
      this.refreshBtn = this.querySelector(".refresh-alsolike");
      this.currentIndex = 0;
      this.validItems = [];
      this.currentRenderedFingerprint = "";
    }

    connectedCallback() {
      this.style.display = "none";
      this.init();
      if (this.refreshBtn)
        this.refreshBtn.addEventListener("click", () => this.rotateItems());
      this.addEventListener("click", handleSafeAddToCart);
    }

    async init() {
      if (!this.itemsContainer) return;

      const cart = window.ShopifyCartTracker.getCart();
      const cartHandles = (cart.items || [])
        .map((item) => item.handle)
        .filter(Boolean);
      const anchorIds = getSortedCartProductIds(cart.items);

      this.validItems = [];

      for (const anchorId of anchorIds) {
        const allItems = await fetchRecommendations(
          anchorId,
          anchorId == "empty"
            ? CONFIG.CART_EMPTY_SECTION_ID
            : CONFIG.CART_SECTION_ID,
        );
        const filtered = allItems.filter((item) => {
          const handle = extractHandleFromElement(item);
          return (
            handle && !isExcludedHandle(handle) && !cartHandles.includes(handle)
          );
        });
        if (filtered.length > 0) {
          this.validItems = filtered;
          break;
        }
      }

      if (this.validItems.length === 0) {
        const fallbackId = 'empty'//this.dataset.emptyRecommendProductId;
        if (fallbackId) {
          const allItems = await fetchRecommendations(
            fallbackId,
            CONFIG.CART_EMPTY_SECTION_ID,
          );
          this.validItems = allItems.filter((item) => {
            const handle = extractHandleFromElement(item);
            return (
              handle &&
              !isExcludedHandle(handle) &&
              !cartHandles.includes(handle)
            );
          });
        }
      }
      this.renderBatch();
    }

    renderBatch() {
      if (!this.validItems || this.validItems.length === 0) {
        this.style.display = "none";
        this.currentRenderedFingerprint = "";
        return;
      }

      const displayCount = Math.min(
        CONFIG.CART_DISPLAY_LIMIT,
        this.validItems.length,
      );

      let newFingerprint = "";
      for (let i = 0; i < displayCount; i++) {
        const idx = (this.currentIndex + i) % this.validItems.length;
        newFingerprint += extractHandleFromElement(this.validItems[idx]) + ",";
      }

      // 🛡️ Fingerprint Interception Protection: If the expected products are identical, interrupt the process (0 repaints, 0 flickering)
      if (this.currentRenderedFingerprint === newFingerprint) {
        this.style.display = "block";
        return;
      }

      this.currentRenderedFingerprint = newFingerprint;
      this.style.display = "block";
      this.itemsContainer.innerHTML = "";

      for (let i = 0; i < displayCount; i++) {
        const idx = (this.currentIndex + i) % this.validItems.length;
        this.itemsContainer.appendChild(this.validItems[idx].cloneNode(true));
      }

      if (this.refreshBtn) {
        if (this.validItems.length <= CONFIG.CART_DISPLAY_LIMIT) {
          this.refreshBtn.style.display = "none";
        } else {
          this.refreshBtn.style.display = "";
        }
      }

      reviveScripts(this.itemsContainer);
      initCartRecommendVariants(this.itemsContainer);
    }

    rotateItems() {
      if (
        !this.validItems ||
        this.validItems.length <= CONFIG.CART_DISPLAY_LIMIT
      )
        return;
      if (this.refreshBtn) this.refreshBtn.classList.add("refreshing");

      this.currentRenderedFingerprint = "";
      this.currentIndex =
        (this.currentIndex + CONFIG.CART_DISPLAY_LIMIT) %
        this.validItems.length;
      this.renderBatch();
      setTimeout(() => {
        if (this.refreshBtn) this.refreshBtn.classList.remove("refreshing");
      }, 300);
    }
  }

  // ==========================================
  // 5. Component: PDP Product Recommendations (<product-recommendations>)
  // ==========================================
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.groupContainer = this.querySelector(".product-recommend-group");
      this.refreshBtn = this.querySelector(".product-recommend-refresh");
      this.currentIndex = 0;
      this.validItems = [];
      this.currentRenderedFingerprint = "";
    }

    connectedCallback() {
      this.style.display = "none";
      this.init();
      if (this.refreshBtn)
        this.refreshBtn.addEventListener("click", () => this.rotateItems());
      this.addEventListener("click", handleSafeAddToCart);
    }

    async init() {
      if (!this.groupContainer) return;

      const cart = window.ShopifyCartTracker.getCart();
      const cartHandles = (cart.items || [])
        .map((item) => item.handle)
        .filter(Boolean);
      const productSection = document.querySelector(".product-section");
      let productId =
        this.dataset.productId || productSection?.dataset.productId;
      const currentHandle = productSection?.dataset.productHandle;

      if (!productId) {
        this.style.display = "none";
        return;
      }

      const allItems = await fetchRecommendations(
        productId,
        CONFIG.PDP_SECTION_ID,
      );

      this.validItems = allItems.filter((item) => {
        const handle = extractHandleFromElement(item);
        return (
          handle &&
          !isExcludedHandle(handle) &&
          !cartHandles.includes(handle) &&
          handle !== currentHandle
        );
      });

      this.renderBatch();
    }

    renderBatch() {
      if (!this.validItems || this.validItems.length === 0) {
        this.style.display = "none";
        this.currentRenderedFingerprint = "";
        return;
      }

      const displayCount = Math.min(
        CONFIG.PDP_DISPLAY_LIMIT,
        this.validItems.length,
      );

      let newFingerprint = "";
      for (let i = 0; i < displayCount; i++) {
        const idx = (this.currentIndex + i) % this.validItems.length;
        newFingerprint += extractHandleFromElement(this.validItems[idx]) + ",";
      }

      // 🛡️ Fingerprint Interception Protection
      if (this.currentRenderedFingerprint === newFingerprint) {
        this.style.display = "block";
        return;
      }

      this.currentRenderedFingerprint = newFingerprint;
      this.style.display = "block";
      this.groupContainer.innerHTML = "";

      for (let i = 0; i < displayCount; i++) {
        const idx = (this.currentIndex + i) % this.validItems.length;
        this.groupContainer.appendChild(this.validItems[idx].cloneNode(true));
      }

      if (this.refreshBtn) {
        if (this.validItems.length <= CONFIG.PDP_DISPLAY_LIMIT) {
          this.refreshBtn.style.display = "none";
        } else {
          this.refreshBtn.style.display = "";
        }
      }

      stripPdpAnchorFields(this.groupContainer);
      reviveScripts(this.groupContainer);
    }

    rotateItems() {
      if (
        !this.validItems ||
        this.validItems.length <= CONFIG.PDP_DISPLAY_LIMIT
      )
        return;
      this.currentRenderedFingerprint = "";
      this.currentIndex =
        (this.currentIndex + CONFIG.PDP_DISPLAY_LIMIT) % this.validItems.length;
      this.renderBatch();
    }
  }

  // ==========================================
  // 6. Component: Assembly Video Recommendations (<assembly-recommendations>)
  // ==========================================
  class AssemblyRecommendations extends HTMLElement {
    constructor() {
      super();
      this.groupContainer = this.querySelector(
        ".assembly-video-product-recommend-group",
      );
      this.refreshBtn = this.querySelector(".product-recommend-refresh");
      this.pool = [];
      this.renderOffset = 0;
    }

    connectedCallback() {
      if (!this.groupContainer || !this.dataset.productId) return;
      this.init();
      if (this.refreshBtn) {
        this.refreshBtn.addEventListener("click", () => this.rotateItems());
      }
      this.addEventListener("click", handleSafeAddToCart);
      this.addEventListener("change", (e) => {
        if (
          e.target.matches(
            ".product-recommend-item select[name='items[1][id]'], .product-recommend-item select.grid-product-variant-input",
          )
        ) {
          syncAssemblyRecommendVariant(e.target);
        }
      });
    }

    async init() {
      const productId = this.dataset.productId;
      if (!productId) return;

      const allItems = await fetchRecommendations(
        productId,
        CONFIG.CART_SECTION_ID,
      );
      this.pool = allItems
        .map((el) => ({ handle: extractHandleFromElement(el) }))
        .filter((entry) => entry.handle && !isExcludedHandle(entry.handle));

      if (!this.pool.length) {
        this.style.display = "none";
        return;
      }

      this.style.display = "";
      this.renderOffset = 0;
      await this.renderBatch();
    }

    async renderBatch() {
      if (!this.pool.length || !this.groupContainer) return;

      const [products, nextIndex] = await fetchRecommendProductsLimit(
        this.pool,
        this.renderOffset,
        CONFIG.ASSEMBLY_DISPLAY_LIMIT,
      );

      if (!products.length) return;

      if (typeof nextIndex === "number") {
        this.renderOffset = nextIndex % this.pool.length;
      }

      await ensureThemeTemplate("template-video");
      if (typeof theme.templates["template-video"].result !== "function")
        return;

      this.groupContainer.innerHTML = "";
      products.forEach((item) => {
        if (!item.product) return;
        this.groupContainer.insertAdjacentHTML(
          "beforeend",
          theme.templates["template-video"].result(item.product),
        );
      });

      initAssemblyRecommendVariants(this.groupContainer);
    }

    async rotateItems() {
      if (
        !this.pool.length ||
        this.pool.length <= CONFIG.ASSEMBLY_DISPLAY_LIMIT
      )
        return;
      await this.renderBatch();
    }
  }

  // ==========================================
  // 7. Component: Seedling Tool Recommendations (<seedling-recommendations>)
  // ==========================================
  class SeedlingRecommendations extends HTMLElement {
    constructor() {
      super();
      this.itemsContainer = this.querySelector(".grid-product__group");
    }

    connectedCallback() {
      this.addEventListener("click", handleSafeAddToCart);
    }

    async load(handleInput) {
      const handle = normalizeHandleInput(handleInput);
      if (!handle || !this.itemsContainer) return;

      const data = await ajaxSingleProduct({ handle });
      if (!data?.product?.id) return;

      const allItems = await fetchRecommendations(
        data.product.id,
        CONFIG.CART_SECTION_ID,
      );
      const handles = allItems
        .map((el) => extractHandleFromElement(el))
        .filter((h) => h && !isExcludedHandle(h))
        .slice(0, CONFIG.SEEDLING_SECTION_LIMIT);

      if (!handles.length) {
        this.itemsContainer.innerHTML = "";
        return;
      }

      const routePrefix = window.route_prefix || "/";
      const templates = await Promise.all(
        handles.map((productHandle) =>
          fetch(`${routePrefix}products/${productHandle}?view=section`, {
            credentials: "same-origin",
          }).then((res) => (res.ok ? res.text() : "")),
        ),
      );

      this.itemsContainer.innerHTML = "";
      templates.filter(Boolean).forEach((html) => {
        this.itemsContainer.insertAdjacentHTML(
          "beforeend",
          `<div class="flickity-item">${html}</div>`,
        );
      });

      initSeedlingRecommendCarousel(this.itemsContainer);
    }
  }

  // ==========================================
  // 8. Registration and Global API Exposure
  // ==========================================
  if (!customElements.get("cart-recommendations")) {
    customElements.define("cart-recommendations", CartRecommendations);
  }
  if (!customElements.get("product-recommendations")) {
    customElements.define("product-recommendations", ProductRecommendations);
  }
  if (!customElements.get("assembly-recommendations")) {
    customElements.define("assembly-recommendations", AssemblyRecommendations);
  }
  if (!customElements.get("seedling-recommendations")) {
    customElements.define("seedling-recommendations", SeedlingRecommendations);
  }

  window.CartRecommendationsAPI = {
    refresh: function () {
      document
        .querySelectorAll("cart-recommendations")
        .forEach((el) => el.init());
    },
  };
  window.ProductRecommendationsAPI = {
    refresh: function () {
      document
        .querySelectorAll("product-recommendations")
        .forEach((el) => el.init());
    },
  };
  window.SeedlingRecommendationsAPI = {
    load: function (handle) {
      const el = document.querySelector("seedling-recommendations");
      if (el) el.load(handle);
    },
  };
})();
